import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { useAppContext } from '../AppContext';

const CommentForm = ({ entryId, parentId = null, onCommentAdded, contentType = 'whisper', nexusId = null, initialContent = '', parentPath = null, nestingLevel = 0, topLevelParentId = null }) => {
  const { t, i18n } = useTranslation();
  const { appFunctions, setMessage, userProfiles, userId, db, doc: fbDoc, getDoc: fbGetDoc, appId } = useAppContext();
  const [comment, setComment] = useState(initialContent);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [connectedFriends, setConnectedFriends] = useState([]);
  const [commentAuthors, setCommentAuthors] = useState([]);

  useEffect(() => {
    setComment(initialContent);
  }, [initialContent]);

  useEffect(() => {
    const fetchConnectedFriends = async () => {
      if (!userId) return;
      const userConnectionsRef = fbDoc(db, `artifacts/${appId}/public/data/user_profiles/${userId}/connections/friends`);
      const docSnap = await fbGetDoc(userConnectionsRef);
      if (docSnap.exists()) {
        setConnectedFriends(Object.keys(docSnap.data()));
      }
    };

    // For now, we'll assume commentAuthors are fetched elsewhere or passed down.
    // In a real scenario, you might fetch them from the current entry's comments subcollection.

    fetchConnectedFriends();
  }, [userId, db, appId, fbDoc, fbGetDoc]);



  useEffect(() => {
    if (mentionQuery) {
      const allPossibleMentions = userProfiles.filter(p => 
        (connectedFriends.includes(p.id) || commentAuthors.includes(p.id) || p.id === userId) && // Only friends, comment authors, or self
        p.displayName.toLowerCase().includes(mentionQuery.toLowerCase())
      );
      setMentionSuggestions(allPossibleMentions);
    } else {
      setMentionSuggestions([]);
    }
  }, [mentionQuery, userProfiles]);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setComment(text);

    const atIndex = text.lastIndexOf('@');
    if (atIndex !== -1) {
      const query = text.substring(atIndex + 1);
      setMentionQuery(query);
    } else {
      setMentionQuery('');
    }
  };

  const handleMentionSelect = (user) => {
    const atIndex = comment.lastIndexOf('@');
    const newComment = `${comment.substring(0, atIndex)}@[${user.displayName}](${user.id}) `;
    setComment(newComment);
    setMentionQuery('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      setMessage(t('commentForm.emptyError'));
      return;
    }

    setIsSubmitting(true);
    try {
      const createComment = httpsCallable(appFunctions, 'createComment');
      
      // Extract tagged user IDs
      const taggedUserIds = [];
      const mentionRegex = /@\[([^\]]+)\]\(([^\)]+)\)/g;
      let match;
      let cleanedContent = comment.trim();
      while ((match = mentionRegex.exec(comment.trim())) !== null) {
        taggedUserIds.push(match[2]);
        cleanedContent = cleanedContent.replace(match[0], `@${match[1]}`); // Replace with display name for storage
      }

      await createComment({
        entryId: entryId,
        content: cleanedContent,
        parentId: nestingLevel === 2 ? topLevelParentId : parentId,
        parentPath: parentPath, // Pass the full path to the parent
        isAnonymous: false,
        contentType: contentType,
        nexusId: nexusId,
        language: i18n.language,
        taggedUserIds: taggedUserIds,
      });

      setComment('');
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (error) {
      setMessage(t('commentForm.postError', { error: error.message }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormDisabled = nestingLevel === 2 && parentId !== topLevelParentId;
  if (isFormDisabled) {
    return <p className="text-gray-400 italic text-sm mt-4">{t('commentForm.maxNestingReached')}</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-800/50 p-3 rounded-lg border border-gray-700/80">
      <textarea
        value={comment}
        onChange={handleInputChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 200)}
        placeholder={t('commentForm.placeholder')}
        className={`w-full bg-gray-900/50 p-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none placeholder-gray-400 text-white transition-all duration-300 ${isFocused ? 'rows-4' : 'rows-2'}`}
        rows={isFocused ? 4 : 2}
      />
      {mentionSuggestions.length > 0 && isFocused && (
        <div className="bg-gray-700 rounded-lg mt-2 p-2 max-h-40 overflow-y-auto">
          {mentionSuggestions.map(user => (
            <div key={user.id} onClick={() => handleMentionSelect(user)} className="flex items-center p-2 hover:bg-gray-600 cursor-pointer rounded-md text-sm">
              <img src={user.photoURL || "https://placehold.co/24x24/AEC6CF/FFFFFF?text=U"} alt={user.displayName} className="w-6 h-6 rounded-full mr-2" />
              <span>{user.displayName}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end items-center mt-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-1.5 bg-blue-500 text-white text-sm font-semibold rounded-full hover:bg-blue-600 transition duration-300 disabled:opacity-50"
        >
          {isSubmitting ? t('commentForm.posting') : (parentId ? t('commentForm.postReply') : t('commentForm.postComment'))}
        </button>
      </div>
    </form>
  );
};


export default CommentForm;
